/**
 * TeamTab Component
 *
 * Dedicated tab for team mode configuration.
 * Shows when agent_mode is 'TEAM_LEADER'.
 */

import React, { useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { FieldSection } from '@/components/fields/FieldSection';
import { FIELD_CONFIG } from '@/config/fieldConfig';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import type { AgentConfiguration } from '@/types';

// Team-related sections
const TEAM_SECTIONS = ['team'];

interface TeamTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const TeamTab: React.FC<TeamTabProps> = ({
  config: _config, // Kept for interface consistency
  onChange,
  appContext,
}) => {
  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (TEAM_SECTIONS.includes(section)) {
        if (!groups[section]) {
          groups[section] = [];
        }
        groups[section].push({ name, schema });
      }
    });

    return groups;
  }, []);

  // Check if a section has any visible fields
  const sectionHasVisibleFields = useCallback((sectionKey: string) => {
    const fields = groupedFields[sectionKey];
    if (!fields || fields.length === 0) return false;

    return fields.some((field) => {
      if (!field.schema.showIf) return true;
      return field.schema.showIf(appContext);
    });
  }, [groupedFields, appContext]);

  // Sort sections by TEAM_SECTIONS order
  const sortedSections = useMemo(() => {
    return TEAM_SECTIONS
      .filter(key => FIELD_SECTIONS[key] && groupedFields[key]?.length > 0)
      .map(key => [key, FIELD_SECTIONS[key]] as [string, typeof FIELD_SECTIONS[string]]);
  }, [groupedFields]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">团队模式配置</h2>
          <p className="text-sm text-slate-500 mt-1">
            配置多Agent协作和任务委派
          </p>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sortedSections.map(([sectionKey, sectionConfig]) => {
          if (!sectionHasVisibleFields(sectionKey)) {
            return null;
          }
          return (
            <div
              key={sectionKey}
              className={sectionConfig.layout === 1 ? 'xl:col-span-2' : ''}
            >
              <FieldSection
                sectionKey={sectionKey}
                sectionConfig={sectionConfig}
                fields={groupedFields[sectionKey] || []}
                values={appContext}
                onChange={onChange}
                appContext={appContext}
              />
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <Card className="p-4 bg-purple-50 border-purple-100">
        <h4 className="text-sm font-medium text-purple-800 mb-2">团队模式说明</h4>
        <ul className="text-xs text-purple-700 space-y-1">
          <li>• <strong>Team Members</strong>: 配置团队成员Agent列表</li>
          <li>• <strong>Respond Directly</strong>: Leader直接响应不委派</li>
          <li>• <strong>Delegate to All</strong>: 同时委派给所有成员</li>
          <li>• <strong>Share Interactions</strong>: 成员间共享交互信息</li>
          <li>• <strong>Show Member Responses</strong>: 向用户展示成员响应</li>
        </ul>
      </Card>
    </div>
  );
};

export default TeamTab;
